/* eslint-disable import/no-restricted-paths */
import { USER_ROLES } from "@nlc/shared-types";
import { Link, useLocation } from "@tanstack/react-router";
import { useFeatureFlagEnabled } from "posthog-js/react";

import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";
import { useUser } from "@/hooks/useUser";
import { useWhitelabel } from "@/hooks/useWhitelabel";
import { ALPHA_FLAG } from "@/lib/posthog-flags";
import { cn } from "@/utils/cn";

import { Sheet } from "../sheet";
import { CurrentPlan } from "./components/current-plan";
import { HelpSection } from "./components/help-section";
import { NavGroup } from "./components/nav-group";
import { NavItem } from "./components/nav-item";
import { TeamMembersNavItem } from "./components/team-members-nav-item";
import { isPathActive } from "./is-path-active";
import { ITEM_AI_AD_STUDIO, NAV_ITEMS, NLC_ONLY_ITEMS } from "./navItems";
import { useSidebar } from "./sidebar-context";

interface NavItemsProps {
  isCollapsed?: boolean;
}

function NavItems({ isCollapsed = false }: NavItemsProps) {
  const { currentProject, data: user, websiteUrl } = useUser();
  const { isWhitelabel } = useWhitelabel();
  const location = useLocation();
  const alphaEnabled = useFeatureFlagEnabled(ALPHA_FLAG) === true;
  const isKickstarter = user?.user.accountType === "kickstarter";

  if (!user) return null;

  const baseNavItems = NAV_ITEMS[user.user.roleName as keyof typeof NAV_ITEMS];

  if (!baseNavItems) return null;

  const shouldFilterNlcItems =
    isWhitelabel && user.user.roleName !== USER_ROLES.OWNER;
  const projectId = currentProject?.id || user.currentProjectId;

  const navItems = baseNavItems.filter(
    (item) =>
      (!shouldFilterNlcItems || !NLC_ONLY_ITEMS.has(item)) &&
      (alphaEnabled || item !== ITEM_AI_AD_STUDIO),
  );

  return (
    <nav className="h-full overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y">
      <ul className="flex flex-col gap-1 p-1">
        {navItems.map((item) => {
          if ("children" in item) {
            return (
              <NavGroup
                icon={item.icon}
                isCollapsed={isCollapsed}
                items={item.children}
                key={item.label}
                label={item.label}
                pathname={location.pathname}
              />
            );
          }

          const itemBasePath = item.href.split("?")[0];
          const isActive = isPathActive(location.pathname, item.href);

          if (
            item.href === "/users" &&
            user.user.roleName === USER_ROLES.TEAM_LEADER
          ) {
            return (
              <TeamMembersNavItem
                isActive={isActive}
                isCollapsed={isCollapsed}
                key={item.href}
                userId={user.user.id}
              />
            );
          }

          if (itemBasePath === "/my-files") {
            return (
              <li
                className={cn(
                  "hover:bg-bg-active rounded-sm w-full",
                  isActive && "bg-bg-active ring-4 ring-gray-400/[0.14]",
                )}
                key={item.href}
              >
                <Link
                  className={cn(
                    "flex p-1.5 text-sm font-semibold text-text-secondary hover:text-text-primary outline-hidden",
                    isCollapsed ? "justify-center px-2xl" : "gap-md px-lg",
                    isActive && "text-text-primary",
                    isKickstarter &&
                      "opacity-50 cursor-not-allowed pointer-events-none select-none",
                  )}
                  search={{ projectId: projectId ?? undefined }}
                  to="/my-files"
                >
                  <item.icon
                    className={cn(
                      "size-5 shrink-0 text-nav-item-icon-fg",
                      isActive && "text-fg-brand-primary",
                    )}
                  />
                  {!isCollapsed && (
                    <span className="whitespace-nowrap">{item.label}</span>
                  )}
                </Link>
              </li>
            );
          }

          if (itemBasePath === "/my-sites") {
            if (websiteUrl) {
              return (
                <NavItem
                  badge="beta"
                  href={websiteUrl}
                  icon={item.icon}
                  isCollapsed={isCollapsed}
                  key={item.href}
                  label={item.label}
                />
              );
            }

            return null;
          }

          return (
            <NavItem
              href={item.href}
              icon={item.icon}
              isActive={isActive}
              isCollapsed={isCollapsed}
              key={item.href}
              label={item.label}
            />
          );
        })}
      </ul>
    </nav>
  );
}

function SidebarContent() {
  const { isWhitelabel } = useWhitelabel();
  const { hasRole } = useRoleAccess();
  const { isAdmin, isCollaborator, isOwner } = {
    isAdmin: hasRole(["admin"]),
    isCollaborator: hasRole(["collaborator"]),
    isOwner: hasRole(["owner"]),
  };

  return (
    <div className="grid h-full grid-rows-[1fr_auto]">
      <div className="min-h-0 overflow-hidden px-2">
        <NavItems />
      </div>

      <div>
        {!isCollaborator && !(isWhitelabel && isAdmin) && <CurrentPlan />}

        {(!isWhitelabel || isOwner) && (
          <div className="px-xl pb-xl">
            <HelpSection />
          </div>
        )}
      </div>
    </div>
  );
}

function IconOnlySidebar() {
  return (
    <div className="flex h-full flex-col pt-3xl">
      <NavItems isCollapsed />
    </div>
  );
}

export function Sidebar() {
  const {
    isHovered,
    isMobile,
    openMobile,
    setIsHovered,
    setOpenMobile,
    state,
  } = useSidebar();
  const isCollapsed = state === "collapsed";

  if (isMobile) {
    return (
      <Sheet
        classNames={{
          content: "h-[calc(100dvh-var(--spacing-navbar))]",
          overlay: "top-navbar",
        }}
        isOpen={openMobile}
        onOpenChange={setOpenMobile}
        side="left"
      >
        <div className="h-full overflow-hidden border-r border-border-secondary bg-bg-primary pt-3xl w-[170px]">
          <SidebarContent />
        </div>
      </Sheet>
    );
  }

  return (
    <>
      <div
        className={cn(
          "fixed top-navbar h-[calc(100dvh-var(--spacing-navbar))] overflow-x-hidden touch-none border-r-2 border-border-secondary bg-bg-primary pt-3xl z-50",
          "transition-all duration-300 ease-in-out",
          isCollapsed
            ? "w-[64px] -translate-x-full"
            : "w-[170px] translate-x-0",
        )}
      >
        <div className="h-full w-[170px]">
          <SidebarContent />
        </div>
      </div>

      <div
        className={cn(
          "fixed top-navbar h-[calc(100dvh-var(--spacing-navbar))] w-[64px] overflow-hidden border-r-2 border-border-secondary bg-bg-primary z-50",
          "transition-all duration-300 ease-in-out",
          isCollapsed && isHovered
            ? "translate-x-0 opacity-100"
            : "-translate-x-full opacity-0 pointer-events-none",
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <IconOnlySidebar />
      </div>
    </>
  );
}

export { SidebarProvider, useSidebar } from "./sidebar-context";
